// Array-function argument column affinity (pgsql script.13 §2/§4).
//
// `'Trailers' = ANY(f.▮)` / `array_length(f.▮, 1)` — the argument names an
// array-typed column, but the plain alias-dot listing ranks all columns
// alphabetically and buries `special_features`. Columns whose declared type
// is an array (`_text`, `text[]`) are lifted to tier 1 at that slot; the rest
// of the tier-1 column block drops to 2 (same shape as the JSON-function
// affinity in ./jsonFunctionArgAffinity.ts).
//
// Wired in pgsql only: Postgres is the sole engine with array-typed columns,
// so the signal is vacuous elsewhere — no divergence in ranking POLICY, just
// no array columns to rank. If another engine grows array types, wire the
// same helper there.

const ARRAY_FN_NAMES: readonly string[] = [
  'ANY',
  'ALL',
  'array_length',
  'array_upper',
  'array_lower',
  'array_ndims',
  'array_dims',
  'cardinality',
  'unnest',
  'array_to_string',
  'array_position',
  'array_positions',
  'array_remove',
  'array_replace',
  'array_append',
  'array_prepend',
  'array_cat',
];

const AT_ARRAY_FN_ARG_RE = new RegExp(
  `\\b(?:${ARRAY_FN_NAMES.join('|')})\\s*\\(\\s*[\\w."$]*$`,
  'i',
);

/** True when the caret sits in the first argument of an array-taking function. */
export function isAtArrayFunctionArg(text: string, offset: number, statementStart: number): boolean {
  return AT_ARRAY_FN_ARG_RE.test(text.slice(statementStart, offset));
}

/** Postgres spells array types `_text` (catalog) or `text[]` (SQL). */
export function isArrayDataType(dataType: string): boolean {
  return dataType.startsWith('_') || dataType.endsWith('[]') || /^ARRAY\b/i.test(dataType);
}

/** Lower-cased names of the array-typed columns in `columns`. */
export function collectArrayColumns(
  columns: ReadonlyArray<{ readonly name: string; readonly dataType: string }>,
): Set<string> {
  const out = new Set<string>();
  for (const c of columns) {
    if (isArrayDataType(c.dataType)) out.add(c.name.toLowerCase());
  }
  return out;
}
